import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CrashClawSkillsPanel — Skills 配置子模块
 *
 * 功能：
 *   - 拉取所有平台官方 Skills + 用户自定义 Skills，前端合并成统一展示列表
 *   - 过滤 tab（全部 / 平台官方 / 我管理的）
 *   - Skill 卡片网格展示（参照原型设计），含启用/禁用状态
 *   - 上传 Skill Modal（新建 CUSTOM：名称 + 文件 + 使用说明）
 *   - 预览 Modal（点击卡片弹出：名称 + 使用说明；CUSTOM 有修改/删除按钮）
 *   - 修改 Modal（从预览切换：可编辑名称、重新上传文件、编辑使用说明）
 *
 * 文件上传支持三种格式（SkillFileUploadZone 组件）：
 *   1. .zip 包（根目录须含 SKILL.md）— 直接上传
 *   2. 单个 SKILL.md 文件 — 前端自动用 JSZip 打包为 zip 后上传
 *   3. 包含 SKILL.md 的文件夹 — 前端自动用 JSZip 打包为 zip 后上传
 *   上传区域同时支持点击选择和拖拽（含拖拽目录），后端接口无需改动。
 *
 * 后端接口：
 *   - POST /redir/api/crashclaw/listPlatformSkills → 平台 Skill 列表
 *   - POST /redir/api/crashclaw/listUserSkills     → 用户 Skill 列表
 *   - POST /redir/api/crashclaw/addPlatformSkill   → 采用平台 Skill
 *   - POST /redir/api/crashclaw/createUserSkill    → 新建 CUSTOM Skill 元数据
 *   - POST /redir/api/crashclaw/upsertUserSkillFile → 上传/替换 CUSTOM Skill zip
 *   - POST /redir/api/crashclaw/updateUserSkill    → 更新 Skill 元数据
 *   - POST /redir/api/crashclaw/deleteUserSkill    → 删除 Skill
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Form, Input, message, Modal, Tag, Tooltip, Alert } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, UploadOutlined, CrownOutlined, FolderOpenOutlined, InboxOutlined, DeleteOutlined, FileZipOutlined, FileMarkdownOutlined, FolderOutlined, } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import JSZip from 'jszip';
import { ze } from 'utils/zhEn';
// @ts-ignore
import RestHelper from 'utils/RestHelper';
import style from './style.scss';
// ======================== Constants ========================
const API_PREFIX = '/redir/api/crashclaw';
/** 超管接口前缀，走 api-gateway 的超管放通逻辑 */
const ADMIN_API_PREFIX = '/super-admin-api/super-admin-crashclaw';
const FILTER_TABS = [
    { key: 'all', label: ze('全部', 'All') },
    { key: 'platform', label: ze('平台官方', 'Official') },
    { key: 'custom', label: ze('我管理的', 'My Skills') },
    { key: 'admin_default', label: ze('(超管) 平台默认开启', '(Admin) Default Skills'), adminOnly: true },
];
// ======================== Helpers ========================
/**
 * 合并平台 Skills 和用户 Skills 为统一展示列表。
 * 平台 Skills 分为已采用（在 userSkills 中有 PLATFORM 记录）和未采用两种。
 * 用户自定义 Skills 直接加入列表。
 */
function buildDisplayList(platformSkills, userSkills) {
    const items = [];
    for (const ps of platformSkills) {
        if (ps.autoEnabled) {
            items.push({
                type: 'auto_enabled_platform',
                userSkill: null,
                platformSkill: ps,
                name: ps.skillName,
                description: ps.description,
                enabled: true,
                isAdopted: true,
            });
            continue;
        }
        const adopted = userSkills.find(us => us.sourceType === 'PLATFORM' && us.platformSkillKey === ps.skillKey);
        if (adopted) {
            items.push({
                type: 'adopted_platform',
                userSkill: adopted,
                platformSkill: ps,
                name: ps.skillName,
                description: ps.description || adopted.description,
                enabled: adopted.enabled,
                isAdopted: true,
            });
        }
        else {
            items.push({
                type: 'unadopted_platform',
                userSkill: null,
                platformSkill: ps,
                name: ps.skillName,
                description: ps.description,
                enabled: false,
                isAdopted: false,
            });
        }
    }
    for (const us of userSkills) {
        if (us.sourceType === 'CUSTOM') {
            items.push({
                type: 'custom',
                userSkill: us,
                platformSkill: null,
                name: us.skillName,
                description: us.description,
                enabled: us.enabled,
                isAdopted: true,
            });
        }
    }
    return items;
}
function filterDisplayList(items, tab) {
    if (tab === 'platform')
        return items.filter(i => i.type !== 'custom');
    if (tab === 'custom')
        return items.filter(i => i.type === 'custom');
    return items;
}
function splitPlatformDisplayItems(items) {
    const autoEnabledItems = items.filter(i => i.type === 'auto_enabled_platform');
    const regularPlatformItems = items.filter(i => i.type === 'adopted_platform' || i.type === 'unadopted_platform');
    return { autoEnabledItems, regularPlatformItems };
}
/** 忽略 macOS 压缩包常见的元数据条目 */
function shouldIgnoreZipEntry(path) {
    const normalized = path.replace(/\\/g, '/');
    if (!normalized || normalized.endsWith('/'))
        return true;
    const parts = normalized.split('/').filter(Boolean);
    if (parts[0] === '__MACOSX')
        return true;
    return parts[parts.length - 1] === '.DS_Store';
}
/**
 * 检测 zip 内 SKILL.md 位置。
 * 支持根目录 SKILL.md，或「单个顶层目录/skill-name/SKILL.md」的常见打包方式。
 */
function findSkillMdStripPrefix(entryNames) {
    const files = entryNames
        .map(name => name.replace(/\\/g, '/'))
        .filter(name => !shouldIgnoreZipEntry(name));
    const hasRootSkillMd = files.some(name => {
        const parts = name.split('/').filter(Boolean);
        return parts.length === 1 && parts[0].toLowerCase() === 'skill.md';
    });
    if (hasRootSkillMd)
        return null;
    const skillMdPaths = files.filter(name => {
        const parts = name.split('/').filter(Boolean);
        return parts.length === 2 && parts[1].toLowerCase() === 'skill.md';
    });
    if (skillMdPaths.length !== 1)
        return null;
    const topDir = skillMdPaths[0].split('/').filter(Boolean)[0];
    const prefix = `${topDir}/`;
    const allUnderPrefix = files.every(name => name.startsWith(prefix));
    return allUnderPrefix ? prefix : null;
}
/**
 * 校验 ZIP 是否包含 SKILL.md（根目录或单文件夹包裹）。
 * 与后端 UserCrashClawSkillBusiness.parseSkillZip 的校验逻辑对齐。
 */
function isValidSkillZipLayout(entryNames) {
    if (findSkillMdStripPrefix(entryNames) !== null)
        return true;
    return entryNames.some(name => {
        const normalized = name.replace(/\\/g, '/');
        const parts = normalized.split('/').filter(Boolean);
        return parts.length === 1 && parts[0].toLowerCase() === 'skill.md';
    });
}
async function validateSkillZip(file) {
    try {
        const zip = await JSZip.loadAsync(file);
        return isValidSkillZipLayout(Object.keys(zip.files));
    }
    catch {
        return false;
    }
}
/**
 * 将「文件夹名/SKILL.md」结构的 zip 规范化为根目录 SKILL.md，
 * 确保 COS 存储与 Hermes skills 目录结构一致。
 */
async function normalizeSkillZip(file) {
    const zip = await JSZip.loadAsync(file);
    const stripPrefix = findSkillMdStripPrefix(Object.keys(zip.files));
    if (!stripPrefix)
        return file;
    const out = new JSZip();
    for (const [path, entry] of Object.entries(zip.files)) {
        if (entry.dir || shouldIgnoreZipEntry(path))
            continue;
        const normalized = path.replace(/\\/g, '/');
        if (!normalized.startsWith(stripPrefix))
            continue;
        const stripped = normalized.slice(stripPrefix.length);
        if (!stripped)
            continue;
        const buf = await entry.async('arraybuffer');
        out.file(stripped, buf);
    }
    const blob = await out.generateAsync({ type: 'blob' });
    return new File([blob], file.name, { type: 'application/zip' });
}
/** 上传 zip 文件到 upsertUserSkillFile multipart 接口 */
async function uploadSkillFile(skillUid, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('skillUid', skillUid);
    await RestHelper.mustPost(`${API_PREFIX}/upsertUserSkillFile`, formData);
}
/**
 * 将单个 SKILL.md 文件打包为 zip。
 * 后端要求 zip 根目录下存在 SKILL.md，这里严格按此结构打包。
 */
async function packMdFileToZip(mdFile) {
    const zip = new JSZip();
    const content = await mdFile.arrayBuffer();
    zip.file('SKILL.md', content);
    const blob = await zip.generateAsync({ type: 'blob' });
    return new File([blob], 'SKILL.zip', { type: 'application/zip' });
}
/**
 * 将通过 <input webkitdirectory> 选择的文件列表打包为 zip。
 * webkitRelativePath 格式为 "folderName/sub/file.txt"，需要剥离第一层目录名，
 * 使 zip 内部结构直接为 "sub/file.txt"，与用户手动打 zip 的习惯一致。
 */
async function packFileListToZip(files) {
    const zip = new JSZip();
    const fileArr = Array.from(files);
    // webkitRelativePath 的第一段是被选中的文件夹名，需剥离
    const firstSegment = fileArr[0]?.webkitRelativePath?.split('/')[0] || '';
    for (const f of fileArr) {
        const relativePath = f.webkitRelativePath || f.name;
        const stripped = firstSegment
            ? relativePath.replace(new RegExp(`^${escapeRegExp(firstSegment)}/`), '')
            : relativePath;
        if (!stripped)
            continue;
        const buf = await f.arrayBuffer();
        zip.file(stripped, buf);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    return new File([blob], `${firstSegment || 'skill'}.zip`, { type: 'application/zip' });
}
function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** 检查 webkitdirectory 选择的文件列表根目录是否包含 SKILL.md */
function folderFilesHaveSkillMd(files) {
    const fileArr = Array.from(files);
    const firstSegment = fileArr[0]?.webkitRelativePath?.split('/')[0] || '';
    return fileArr.some(f => {
        const rel = f.webkitRelativePath || f.name;
        const stripped = firstSegment
            ? rel.replace(new RegExp(`^${escapeRegExp(firstSegment)}/`), '')
            : rel;
        const parts = stripped.split('/').filter(Boolean);
        return parts.length === 1 && parts[0].toLowerCase() === 'skill.md';
    });
}
/**
 * 递归读取 FileSystemDirectoryEntry 下的所有文件。
 * 用于处理拖拽目录场景（webkitGetAsEntry API）。
 * readEntries 规范要求分批返回结果，需循环调用直到返回空数组。
 */
async function readAllEntriesRecursive(dirEntry, pathPrefix) {
    const results = [];
    const reader = dirEntry.createReader();
    const readBatch = () => new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    let batch;
    do {
        batch = await readBatch();
        for (const entry of batch) {
            if (entry.isFile) {
                const fileEntry = entry;
                const file = await new Promise((resolve, reject) => fileEntry.file(resolve, reject));
                results.push({ path: pathPrefix + entry.name, file });
            }
            else if (entry.isDirectory) {
                const subResults = await readAllEntriesRecursive(entry, pathPrefix + entry.name + '/');
                results.push(...subResults);
            }
        }
    } while (batch.length > 0);
    return results;
}
/**
 * 将拖拽获得的目录文件列表打包为 zip。
 * path 已经是相对于被拖入目录的内部路径（不含目录自身名称）。
 */
async function packDraggedFolderToZip(entries, folderName) {
    const zip = new JSZip();
    for (const { path, file } of entries) {
        const buf = await file.arrayBuffer();
        zip.file(path, buf);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    return new File([blob], `${folderName}.zip`, { type: 'application/zip' });
}
// ======================== SkillFileUploadZone ========================
/**
 * 统一的 Skill 文件上传区域组件，支持三种上传方式：
 * 1. 选择/拖拽 .zip 包（直接使用）
 * 2. 选择/拖拽 SKILL.md 文件（前端用 JSZip 打包为 zip）
 * 3. 选择/拖拽包含 SKILL.md 的文件夹（前端用 JSZip 打包为 zip）
 *
 * 无论哪种方式，最终 onFileChange 回调返回的都是 zip File 对象，
 * 后端接口 upsertUserSkillFile 不需要任何改动。
 */
const SkillFileUploadZone = ({ fileInfo, onFileChange, required }) => {
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [processing, setProcessing] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    /** 处理选择的单个文件（.zip 或 .md） */
    async function handleFileSelected(file) {
        const name = file.name.toLowerCase();
        if (name.endsWith('.zip')) {
            setProcessing(true);
            try {
                const valid = await validateSkillZip(file);
                if (!valid) {
                    message.error(ze('ZIP 包中未找到 SKILL.md。请确保根目录或单个顶层文件夹内包含 SKILL.md', 'SKILL.md not found in ZIP. Ensure it is at the root or inside a single top-level folder.'));
                    return;
                }
                const normalized = await normalizeSkillZip(file);
                onFileChange({ file: normalized, displayName: file.name, sourceType: 'zip' });
            }
            finally {
                setProcessing(false);
            }
            return;
        }
        if (name === 'skill.md') {
            setProcessing(true);
            try {
                const zipped = await packMdFileToZip(file);
                onFileChange({ file: zipped, displayName: file.name, sourceType: 'md' });
            }
            finally {
                setProcessing(false);
            }
            return;
        }
        message.error(ze('请选择 .zip 文件或 SKILL.md 文件', 'Please select a .zip file or SKILL.md file'));
    }
    /** 处理 <input webkitdirectory> 选择的文件夹 */
    async function handleFolderSelected(files) {
        if (!files.length)
            return;
        if (!folderFilesHaveSkillMd(files)) {
            message.error(ze('所选文件夹根目录下未找到 SKILL.md 文件', 'SKILL.md not found in the selected folder root directory'));
            return;
        }
        setProcessing(true);
        try {
            const folderName = files[0].webkitRelativePath?.split('/')[0] || 'skill';
            const zipped = await packFileListToZip(files);
            onFileChange({ file: zipped, displayName: folderName, sourceType: 'folder' });
        }
        finally {
            setProcessing(false);
        }
    }
    /** 处理拖拽放下事件。通过 webkitGetAsEntry 区分文件和目录。 */
    async function handleDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const items = e.dataTransfer.items;
        if (!items || items.length === 0)
            return;
        const firstItem = items[0];
        // webkitGetAsEntry 可区分文件和目录
        const entry = firstItem.webkitGetAsEntry?.() || firstItem.getAsEntry?.();
        if (entry?.isDirectory) {
            setProcessing(true);
            try {
                const dirEntry = entry;
                const allFiles = await readAllEntriesRecursive(dirEntry, '');
                const hasSkillMd = allFiles.some(({ path }) => {
                    const parts = path.split('/').filter(Boolean);
                    return parts.length === 1 && parts[0].toLowerCase() === 'skill.md';
                });
                if (!hasSkillMd) {
                    message.error(ze('拖入的文件夹根目录下未找到 SKILL.md 文件', 'SKILL.md not found in the dropped folder root directory'));
                    return;
                }
                const zipped = await packDraggedFolderToZip(allFiles, entry.name);
                onFileChange({ file: zipped, displayName: entry.name, sourceType: 'folder' });
            }
            finally {
                setProcessing(false);
            }
            return;
        }
        // 单文件拖入
        const file = firstItem.getAsFile?.();
        if (file) {
            await handleFileSelected(file);
        }
    }
    function handleDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }
    function handleDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }
    const sourceTypeIcon = {
        zip: _jsx(FileZipOutlined, { style: { color: '#faad14' } }),
        md: _jsx(FileMarkdownOutlined, { style: { color: '#1890ff' } }),
        folder: _jsx(FolderOutlined, { style: { color: '#52c41a' } }),
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: `${style.skillUploadZone} ${dragOver ? style.skillUploadZoneDragOver : ''} ${processing ? style.skillUploadZoneProcessing : ''}`, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave, children: [_jsx(InboxOutlined, { className: style.skillUploadIcon }), _jsx("div", { className: style.skillUploadHint, children: processing
                            ? ze('正在处理…', 'Processing…')
                            : ze('将 .zip / SKILL.md / 文件夹拖到此处', 'Drop .zip / SKILL.md / folder here') }), _jsxs("div", { className: style.skillUploadBtnRow, children: [_jsx(Button, { size: "small", icon: _jsx(UploadOutlined, {}), onClick: () => fileInputRef.current?.click(), disabled: processing, children: ze('选择文件', 'Select File') }), _jsx(Button, { size: "small", icon: _jsx(FolderOpenOutlined, {}), onClick: () => folderInputRef.current?.click(), disabled: processing, children: ze('选择文件夹', 'Select Folder') })] }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".zip,.md", style: { display: 'none' }, onChange: (e) => {
                            const f = e.target.files?.[0];
                            if (f)
                                handleFileSelected(f);
                            e.target.value = '';
                        } }), _jsx("input", { ref: folderInputRef, type: "file", 
                        // @ts-expect-error webkitdirectory is non-standard but widely supported
                        webkitdirectory: "", style: { display: 'none' }, onChange: (e) => {
                            const files = e.target.files;
                            if (files && files.length > 0)
                                handleFolderSelected(files);
                            e.target.value = '';
                        } })] }), fileInfo && (_jsxs("div", { className: style.skillUploadFileRow, children: [_jsx("span", { className: style.skillUploadFileIcon, children: sourceTypeIcon[fileInfo.sourceType] }), _jsx("span", { className: style.skillUploadFileName, children: fileInfo.displayName }), _jsxs("span", { className: style.skillUploadFileTag, children: [fileInfo.sourceType === 'zip' && 'ZIP', fileInfo.sourceType === 'md' && 'SKILL.md → ZIP', fileInfo.sourceType === 'folder' && ze('文件夹 → ZIP', 'Folder → ZIP')] }), _jsx("button", { className: style.skillUploadFileRemove, onClick: () => onFileChange(null), type: "button", children: _jsx(DeleteOutlined, {}) })] })), _jsx("div", { className: style.skillUploadExtra, children: ze('支持上传 ZIP 包、单个 SKILL.md 文件、或包含 SKILL.md 的文件夹。非 ZIP 格式会自动打包后上传。', 'Supports ZIP packages, single SKILL.md files, or folders containing SKILL.md. Non-ZIP formats are auto-packaged before upload.') })] }));
};
// ======================== Component ========================
const CrashClawSkillsPanel = () => {
    const isSuperAdmin = useSelector((state) => {
        try {
            return !!state.user.get('current').get('isSuper');
        }
        catch {
            return false;
        }
    });
    const [platformSkills, setPlatformSkills] = useState([]);
    const [userSkills, setUserSkills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterTab, setFilterTab] = useState('all');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadFileInfo, setUploadFileInfo] = useState(null);
    // --- 超管：平台默认 Skill 管理状态 ---
    const [adminSkills, setAdminSkills] = useState([]);
    const [adminModalVisible, setAdminModalVisible] = useState(false);
    const [adminModalMode, setAdminModalMode] = useState('create');
    const [adminSelectedSkill, setAdminSelectedSkill] = useState(null);
    const [adminSubmitting, setAdminSubmitting] = useState(false);
    const [adminUploadFileInfo, setAdminUploadFileInfo] = useState(null);
    /** 危险操作确认 Modal：需输入 Skill 名称才能确认 */
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [confirmInput, setConfirmInput] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmSkillName, setConfirmSkillName] = useState('');
    const [confirmTitle, setConfirmTitle] = useState('');
    const [form] = Form.useForm();
    const [adminForm] = Form.useForm();
    // -------------------- Data fetching --------------------
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [platformRsp, userRsp] = await Promise.all([
                RestHelper.mustPost(`${API_PREFIX}/listPlatformSkills`),
                RestHelper.mustPost(`${API_PREFIX}/listUserSkills`),
            ]);
            setPlatformSkills(platformRsp.json.data || []);
            setUserSkills(userRsp.json.data || []);
        }
        catch (e) {
            console.error('Failed to fetch skills data', e);
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    const displayList = buildDisplayList(platformSkills, userSkills);
    const filteredList = filterDisplayList(displayList, filterTab);
    // -------------------- Card actions --------------------
    async function handleAdoptPlatformSkill(platformSkillKey) {
        try {
            await RestHelper.mustPost(`${API_PREFIX}/addPlatformSkill`, { platformSkillKey });
            message.success(ze('已启用', 'Enabled'));
            await fetchData();
        }
        catch (e) {
            console.error(e);
            message.error(ze('操作失败', 'Operation failed'));
        }
    }
    async function handleToggleEnabled(skill) {
        if (!skill.userSkill)
            return;
        const newEnabled = !skill.enabled;
        // 配置流程简化：平台技能默认开启且不可关闭。仍然调一次 togglePlatformSkill mock 接口走 403，
        // 保证前后端行为一致（即使前端被绕过，后端也会拒绝）。
        const isPlatform = skill.type !== 'custom';
        if (isPlatform && !newEnabled) {
            message.warning(ze('平台技能不可关闭', 'Platform skills cannot be disabled'));
            // 触发一次 mock 校验，不阻塞 UI
            RestHelper.mustPost(`${API_PREFIX}/togglePlatformSkill`, {
                skillUid: skill.userSkill.skillUid,
                enabled: newEnabled,
            }).catch(() => { });
            return;
        }
        try {
            await RestHelper.mustPost(`${API_PREFIX}/updateUserSkill`, {
                skillUid: skill.userSkill.skillUid,
                enabled: newEnabled,
            });
            setUserSkills(prev => prev.map(s => (s.skillUid === skill.userSkill.skillUid ? { ...s, enabled: newEnabled } : s)));
        }
        catch (e) {
            console.error(e);
            message.error(ze('操作失败', 'Operation failed'));
        }
    }
    function handleFooterAction(e, skill) {
        e.stopPropagation();
        if (skill.type === 'unadopted_platform' && skill.platformSkill) {
            handleAdoptPlatformSkill(skill.platformSkill.skillKey);
        }
        else {
            handleToggleEnabled(skill);
        }
    }
    // -------------------- Modal lifecycle --------------------
    function openCreateModal() {
        form.resetFields();
        setUploadFileInfo(null);
        setModalMode('create');
        setSelectedSkill(null);
        setModalVisible(true);
    }
    function openPreviewModal(skill) {
        setSelectedSkill(skill);
        setModalMode('preview');
        setModalVisible(true);
    }
    function switchToEditMode() {
        if (!selectedSkill?.userSkill)
            return;
        form.resetFields();
        setUploadFileInfo(null);
        form.setFieldsValue({
            skillName: selectedSkill.userSkill.skillName,
            description: selectedSkill.userSkill.description || '',
        });
        setModalMode('edit');
    }
    function closeModal() {
        setModalVisible(false);
        setSelectedSkill(null);
        setUploadFileInfo(null);
        setModalMode('create');
    }
    // -------------------- Create --------------------
    async function handleCreateSubmit() {
        try {
            const values = await form.validateFields();
            if (!uploadFileInfo) {
                message.error(ze('请上传 Skill 文件', 'Please upload a Skill file'));
                return;
            }
            setSubmitting(true);
            const createRsp = await RestHelper.mustPost(`${API_PREFIX}/createUserSkill`, {
                skillName: values.skillName,
                description: values.description || null,
            });
            const skillUid = createRsp.json.data.skillUid;
            try {
                await uploadSkillFile(skillUid, uploadFileInfo.file);
            }
            catch (uploadErr) {
                try {
                    await RestHelper.mustPost(`${API_PREFIX}/deleteUserSkill`, { skillUid });
                }
                catch { /* cleanup best-effort */ }
                throw uploadErr;
            }
            message.success(ze('Skill 创建成功', 'Skill created'));
            closeModal();
            await fetchData();
        }
        catch (e) {
            console.error(e);
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            message.error(ze('创建失败，请检查是否包含 SKILL.md', 'Creation failed. Ensure SKILL.md is included'));
        }
        finally {
            setSubmitting(false);
        }
    }
    // -------------------- Edit --------------------
    async function handleEditSubmit() {
        if (!selectedSkill?.userSkill)
            return;
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            const { skillUid } = selectedSkill.userSkill;
            await RestHelper.mustPost(`${API_PREFIX}/updateUserSkill`, {
                skillUid,
                skillName: values.skillName,
                description: values.description || null,
            });
            if (uploadFileInfo) {
                await uploadSkillFile(skillUid, uploadFileInfo.file);
            }
            message.success(ze('Skill 更新成功', 'Skill updated'));
            closeModal();
            await fetchData();
        }
        catch (e) {
            console.error(e);
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            message.error(ze('更新失败', 'Update failed'));
        }
        finally {
            setSubmitting(false);
        }
    }
    // -------------------- Delete --------------------
    function handleDeleteSkill() {
        if (!selectedSkill?.userSkill)
            return;
        const { skillUid } = selectedSkill.userSkill;
        const { name } = selectedSkill;
        Modal.confirm({
            title: ze('确认删除', 'Confirm Deletion'),
            icon: _jsx(ExclamationCircleOutlined, {}),
            content: ze(`确认要删除 Skill "${name}" 吗？删除后不可恢复。`, `Are you sure you want to delete Skill "${name}"? This action cannot be undone.`),
            okText: ze('删除', 'Delete'),
            okButtonProps: { danger: true },
            cancelText: ze('取消', 'Cancel'),
            onOk: async () => {
                await RestHelper.mustPost(`${API_PREFIX}/deleteUserSkill`, { skillUid });
                message.success(ze('已删除', 'Deleted'));
                closeModal();
                fetchData();
            },
        });
    }
    // ==================== 超管：平台默认 Skill 管理 ====================
    const fetchAdminSkills = useCallback(async () => {
        if (!isSuperAdmin)
            return;
        try {
            const rsp = await RestHelper.mustPost(`${ADMIN_API_PREFIX}/listPlatformSkills`, { autoEnabled: true });
            setAdminSkills(rsp.json.data || []);
        }
        catch (e) {
            console.error('Failed to fetch admin skills', e);
        }
    }, [isSuperAdmin]);
    useEffect(() => {
        if (isSuperAdmin && filterTab === 'admin_default') {
            fetchAdminSkills();
        }
    }, [isSuperAdmin, filterTab, fetchAdminSkills]);
    /** 触发危险操作确认：用户必须输入 Skill 名称才能执行 */
    function openConfirmModal(title, skillName, action) {
        setConfirmTitle(title);
        setConfirmSkillName(skillName);
        setConfirmAction(() => action);
        setConfirmInput('');
        setConfirmModalVisible(true);
    }
    async function executeConfirmAction() {
        if (confirmAction) {
            await confirmAction();
        }
        setConfirmModalVisible(false);
    }
    function openAdminCreateModal() {
        adminForm.resetFields();
        setAdminUploadFileInfo(null);
        setAdminModalMode('create');
        setAdminSelectedSkill(null);
        setAdminModalVisible(true);
    }
    function openAdminPreviewModal(skill) {
        setAdminSelectedSkill(skill);
        setAdminModalMode('preview');
        setAdminModalVisible(true);
    }
    function switchAdminToEditMode() {
        if (!adminSelectedSkill)
            return;
        adminForm.resetFields();
        setAdminUploadFileInfo(null);
        adminForm.setFieldsValue({
            skillKey: adminSelectedSkill.skillKey,
            skillName: adminSelectedSkill.skillName,
            description: adminSelectedSkill.description || '',
            category: adminSelectedSkill.category || '',
        });
        setAdminModalMode('edit');
    }
    function closeAdminModal() {
        setAdminModalVisible(false);
        setAdminSelectedSkill(null);
        setAdminUploadFileInfo(null);
        setAdminModalMode('create');
    }
    /** 上传 zip 到超管平台 Skill 的 multipart 接口 */
    async function uploadAdminSkillFile(skillKey, file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('skillKey', skillKey);
        await RestHelper.mustPost(`${ADMIN_API_PREFIX}/upsertPlatformSkillFile`, formData);
    }
    async function handleAdminCreateSubmit() {
        try {
            const values = await adminForm.validateFields();
            if (!adminUploadFileInfo) {
                message.error(ze('请上传 Skill 文件', 'Please upload a Skill file'));
                return;
            }
            const doCreate = async () => {
                setAdminSubmitting(true);
                try {
                    await RestHelper.mustPost(`${ADMIN_API_PREFIX}/createPlatformSkill`, {
                        skillKey: values.skillKey,
                        skillName: values.skillName,
                        description: values.description || null,
                        category: values.category || null,
                        autoEnabled: true,
                    });
                    try {
                        await uploadAdminSkillFile(values.skillKey, adminUploadFileInfo.file);
                    }
                    catch (uploadErr) {
                        try {
                            await RestHelper.mustPost(`${ADMIN_API_PREFIX}/deletePlatformSkill`, { skillKey: values.skillKey });
                        }
                        catch { /* best-effort */ }
                        throw uploadErr;
                    }
                    message.success(ze('平台 Skill 创建成功', 'Platform skill created'));
                    closeAdminModal();
                    await fetchAdminSkills();
                }
                catch (e) {
                    console.error(e);
                    message.error(ze('创建失败，请检查是否包含 SKILL.md', 'Creation failed. Ensure SKILL.md is included'));
                }
                finally {
                    setAdminSubmitting(false);
                }
            };
            openConfirmModal(ze('确认创建平台 Skill', 'Confirm Create Platform Skill'), values.skillName, doCreate);
        }
        catch { /* form validation error */ }
    }
    async function handleAdminEditSubmit() {
        if (!adminSelectedSkill)
            return;
        try {
            const values = await adminForm.validateFields();
            const doUpdate = async () => {
                setAdminSubmitting(true);
                try {
                    await RestHelper.mustPost(`${ADMIN_API_PREFIX}/updatePlatformSkill`, {
                        skillKey: adminSelectedSkill.skillKey,
                        skillName: values.skillName,
                        description: values.description || null,
                        category: values.category || null,
                    });
                    if (adminUploadFileInfo) {
                        await uploadAdminSkillFile(adminSelectedSkill.skillKey, adminUploadFileInfo.file);
                    }
                    message.success(ze('平台 Skill 更新成功', 'Platform skill updated'));
                    closeAdminModal();
                    await fetchAdminSkills();
                }
                catch (e) {
                    console.error(e);
                    message.error(ze('更新失败', 'Update failed'));
                }
                finally {
                    setAdminSubmitting(false);
                }
            };
            openConfirmModal(ze('确认修改平台 Skill', 'Confirm Update Platform Skill'), adminSelectedSkill.skillName, doUpdate);
        }
        catch { /* form validation error */ }
    }
    function handleAdminDeleteSkill() {
        if (!adminSelectedSkill)
            return;
        const { skillKey, skillName } = adminSelectedSkill;
        const doDelete = async () => {
            try {
                await RestHelper.mustPost(`${ADMIN_API_PREFIX}/deletePlatformSkill`, { skillKey });
                message.success(ze('已删除', 'Deleted'));
                closeAdminModal();
                fetchAdminSkills();
            }
            catch (e) {
                console.error(e);
                message.error(ze('删除失败', 'Delete failed'));
            }
        };
        openConfirmModal(ze('确认删除平台 Skill', 'Confirm Delete Platform Skill'), skillName, doDelete);
    }
    // -------------------- Render: Card --------------------
    function renderSkillCard(item, idx) {
        const isAutoEnabled = item.type === 'auto_enabled_platform';
        const isPlatform = item.type !== 'custom';
        const isActive = item.enabled && item.isAdopted;
        let statusText;
        let statusEnabled;
        if (isAutoEnabled) {
            statusText = ze('已启用', 'Enabled');
            statusEnabled = true;
        }
        else if (!item.isAdopted) {
            statusText = ze('未启用', 'Not enabled');
            statusEnabled = false;
        }
        else if (item.enabled) {
            statusText = ze('已启用', 'Enabled');
            statusEnabled = true;
        }
        else {
            statusText = ze('已禁用', 'Disabled');
            statusEnabled = false;
        }
        const footerText = (!item.isAdopted || !item.enabled)
            ? ze('启用', 'Enable')
            : ze('禁用', 'Disable');
        // 平台技能已启用时禁用"禁用"按钮（不可关闭）
        const isPlatformActive = isPlatform && !isAutoEnabled && item.isAdopted && item.enabled;
        const key = item.userSkill?.skillUid || item.platformSkill?.skillKey || `skill-${idx}`;
        return (_jsxs("div", { className: `${style.skillCard} ${isActive ? style.skillCardActive : ''}`, onClick: () => openPreviewModal(item), children: [_jsxs("div", { className: style.skillCardBody, children: [_jsxs("div", { className: style.skillCardHeader, children: [_jsxs("div", { className: style.skillCardTitleRow, children: [_jsx("span", { className: style.skillCardIcon, children: "\u2726" }), _jsx("h3", { className: style.skillCardName, children: item.name }), isAutoEnabled && (_jsx(Tag, { color: "orange", className: style.sourceTag, children: ze('默认开启', 'Default') })), isPlatform && !isAutoEnabled && (_jsx(Tag, { color: "blue", className: style.sourceTag, children: ze('官方', 'Official') }))] }), _jsxs("div", { className: style.skillCardStatus, children: [_jsx("span", { className: `${style.skillStatusDot} ${statusEnabled ? style.skillStatusDotEnabled : style.skillStatusDotDisabled}` }), _jsx("span", { className: `${style.skillStatusText} ${statusEnabled ? style.skillStatusTextEnabled : style.skillStatusTextDisabled}`, children: statusText })] })] }), _jsxs("div", { className: style.skillDescSection, children: [_jsx("div", { className: style.skillDescLabel, children: ze('使用说明', 'Description') }), _jsx("div", { className: style.skillDescBlock, children: item.description || _jsx("span", { className: style.skillDescPlaceholder, children: ze('暂无说明', 'No description') }) })] })] }), _jsx("div", { className: style.skillCardFooter, children: isAutoEnabled ? (_jsx(Tooltip, { title: ze('平台默认开启，自动注入，仅可查看', 'Auto-enabled platform skill, view only'), children: _jsx("button", { className: `${style.skillFooterBtn} ${style.skillFooterBtnDisabled}`, onClick: (e) => e.stopPropagation(), disabled: true, children: ze('查看', 'View') }) })) : isPlatformActive ? (_jsx(Tooltip, { title: ze('平台技能默认开启，不可关闭', 'Platform skills are enabled by default and cannot be disabled'), children: _jsx("button", { className: `${style.skillFooterBtn} ${style.skillFooterBtnDisabled}`, onClick: (e) => e.stopPropagation(), disabled: true, children: footerText }) })) : (_jsx("button", { className: style.skillFooterBtn, onClick: (e) => handleFooterAction(e, item), children: footerText })) })] }, key));
    }
    // -------------------- Render: Modal content --------------------
    function renderPreviewContent() {
        if (!selectedSkill)
            return null;
        const isAutoEnabled = selectedSkill.type === 'auto_enabled_platform';
        const isPlatform = selectedSkill.type !== 'custom';
        return (_jsxs("div", { className: style.skillPreview, children: [_jsxs("div", { className: style.skillPreviewName, children: [selectedSkill.name, isAutoEnabled && (_jsx(Tag, { color: "orange", style: { marginLeft: 8 }, children: ze('默认开启', 'Default') })), isPlatform && !isAutoEnabled && (_jsx(Tag, { color: "blue", style: { marginLeft: 8 }, children: ze('官方', 'Official') }))] }), isAutoEnabled && (_jsx(Alert, { type: "info", showIcon: true, message: ze('此 Skill 由平台默认开启并自动注入，仅可查看，不可修改或删除。', 'This skill is auto-enabled by the platform and injected automatically. View only.'), style: { marginBottom: 12 } })), _jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('使用说明', 'Description') }), _jsx("div", { className: style.skillPreviewValue, children: selectedSkill.description || ze('暂无使用说明', 'No description') })] })] }));
    }
    function renderFormContent() {
        return (_jsxs(Form, { form: form, layout: "vertical", children: [_jsx(Form.Item, { name: "skillName", label: ze('名称', 'Name'), rules: [{ required: true, message: ze('请输入 Skill 名称', 'Please enter skill name') }], children: _jsx(Input, { placeholder: ze('例如 我的自定义 Skill', 'e.g. My Custom Skill') }) }), _jsx(Form.Item, { label: ze('Skill 文件', 'Skill File'), required: modalMode === 'create', children: _jsx(SkillFileUploadZone, { fileInfo: uploadFileInfo, onFileChange: setUploadFileInfo, required: modalMode === 'create' }) }), _jsx(Form.Item, { name: "description", label: ze('使用说明', 'Description'), children: _jsx(Input.TextArea, { rows: 4, placeholder: ze('描述此 Skill 的用途和使用场景', 'Describe the purpose and use cases of this skill') }) })] }));
    }
    // -------------------- Render: Modal chrome --------------------
    function getModalTitle() {
        if (modalMode === 'create')
            return ze('上传 Skill', 'Upload Skill');
        if (modalMode === 'edit')
            return ze('修改 Skill', 'Edit Skill');
        return ze('Skill 详情', 'Skill Details');
    }
    function getModalFooter() {
        if (modalMode === 'preview') {
            if (selectedSkill?.type === 'custom') {
                return (_jsxs("div", { className: style.modalFooter, children: [_jsx(Button, { danger: true, onClick: handleDeleteSkill, children: ze('删除', 'Delete') }), _jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: closeModal, children: ze('关闭', 'Close') }), _jsx(Button, { type: "primary", onClick: switchToEditMode, children: ze('修改', 'Edit') })] })] }));
            }
            return (_jsxs("div", { className: style.modalFooter, children: [_jsx("div", {}), _jsx(Button, { onClick: closeModal, children: ze('关闭', 'Close') })] }));
        }
        return (_jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: closeModal, children: ze('取消', 'Cancel') }), _jsx(Button, { type: "primary", onClick: modalMode === 'create' ? handleCreateSubmit : handleEditSubmit, loading: submitting, disabled: modalMode === 'create' && !uploadFileInfo, children: ze('确定', 'OK') })] }));
    }
    // -------------------- Render: Admin card & modal --------------------
    function renderAdminSkillCard(skill) {
        return (_jsxs("div", { className: `${style.skillCard} ${skill.enabled ? style.skillCardActive : ''}`, onClick: () => openAdminPreviewModal(skill), children: [_jsxs("div", { className: style.skillCardBody, children: [_jsxs("div", { className: style.skillCardHeader, children: [_jsxs("div", { className: style.skillCardTitleRow, children: [_jsx(CrownOutlined, { style: { color: '#faad14', marginRight: 4 } }), _jsx("h3", { className: style.skillCardName, children: skill.skillName }), _jsx(Tag, { color: "orange", children: ze('默认', 'Default') })] }), _jsxs("div", { className: style.skillCardStatus, children: [_jsx("span", { className: `${style.skillStatusDot} ${skill.enabled ? style.skillStatusDotEnabled : style.skillStatusDotDisabled}` }), _jsx("span", { className: `${style.skillStatusText} ${skill.enabled ? style.skillStatusTextEnabled : style.skillStatusTextDisabled}`, children: skill.enabled ? ze('已启用', 'Enabled') : ze('已禁用', 'Disabled') })] })] }), _jsxs("div", { className: style.skillDescSection, children: [_jsx("div", { className: style.skillDescLabel, children: ze('使用说明', 'Description') }), _jsx("div", { className: style.skillDescBlock, children: skill.description || _jsx("span", { className: style.skillDescPlaceholder, children: ze('暂无说明', 'No description') }) })] })] }), _jsx("div", { className: style.skillCardFooter, children: _jsxs("span", { style: { fontSize: 12, color: '#999' }, children: ["key: ", skill.skillKey] }) })] }, skill.skillKey));
    }
    function renderAdminPreviewContent() {
        if (!adminSelectedSkill)
            return null;
        return (_jsxs("div", { className: style.skillPreview, children: [_jsxs("div", { className: style.skillPreviewName, children: [adminSelectedSkill.skillName, _jsx(Tag, { color: "orange", style: { marginLeft: 8 }, children: ze('默认', 'Default') })] }), _jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: "Skill Key" }), _jsx("div", { className: style.skillPreviewValue, style: { fontFamily: 'monospace' }, children: adminSelectedSkill.skillKey })] }), _jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('使用说明', 'Description') }), _jsx("div", { className: style.skillPreviewValue, children: adminSelectedSkill.description || ze('暂无', 'None') })] }), _jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('Skill 内容', 'Skill Content') }), _jsx(Input.TextArea, { value: adminSelectedSkill.skillContent || '', readOnly: true, autoSize: { minRows: 4, maxRows: 12 }, style: { fontFamily: 'monospace', fontSize: 12 } })] })] }));
    }
    function renderAdminFormContent() {
        const isCreate = adminModalMode === 'create';
        return (_jsxs(Form, { form: adminForm, layout: "vertical", children: [_jsx(Form.Item, { name: "skillKey", label: "Skill Key", rules: [{ required: true, message: ze('请输入 Skill Key', 'Please enter skill key') }], extra: ze('唯一标识符，创建后不可修改（如 cs-crash-analysis）', 'Unique identifier, cannot be changed after creation'), children: _jsx(Input, { placeholder: "cs-my-skill", disabled: !isCreate, style: { fontFamily: 'monospace' } }) }), _jsx(Form.Item, { name: "skillName", label: ze('名称', 'Name'), rules: [{ required: true, message: ze('请输入名称', 'Please enter name') }], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "description", label: ze('使用说明', 'Description'), children: _jsx(Input.TextArea, { rows: 2 }) }), _jsx(Form.Item, { name: "category", label: ze('分类', 'Category'), children: _jsx(Input, { placeholder: "analysis / reporting / coding" }) }), _jsx(Form.Item, { label: ze('Skill 文件', 'Skill File'), required: isCreate, children: _jsx(SkillFileUploadZone, { fileInfo: adminUploadFileInfo, onFileChange: setAdminUploadFileInfo, required: isCreate }) })] }));
    }
    function getAdminModalTitle() {
        if (adminModalMode === 'create')
            return ze('创建平台默认 Skill', 'Create Default Platform Skill');
        if (adminModalMode === 'edit')
            return ze('修改平台默认 Skill', 'Edit Default Platform Skill');
        return ze('平台 Skill 详情', 'Platform Skill Details');
    }
    function getAdminModalFooter() {
        if (adminModalMode === 'preview') {
            return (_jsxs("div", { className: style.modalFooter, children: [_jsx(Button, { danger: true, onClick: handleAdminDeleteSkill, children: ze('删除', 'Delete') }), _jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: closeAdminModal, children: ze('关闭', 'Close') }), _jsx(Button, { type: "primary", onClick: switchAdminToEditMode, children: ze('修改', 'Edit') })] })] }));
        }
        return (_jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: closeAdminModal, children: ze('取消', 'Cancel') }), _jsx(Button, { type: "primary", onClick: adminModalMode === 'create' ? handleAdminCreateSubmit : handleAdminEditSubmit, loading: adminSubmitting, disabled: adminModalMode === 'create' && !adminUploadFileInfo, children: ze('确定', 'OK') })] }));
    }
    function renderPlatformSkillSections(items) {
        const { autoEnabledItems, regularPlatformItems } = splitPlatformDisplayItems(items);
        if (autoEnabledItems.length === 0 && regularPlatformItems.length === 0) {
            return (_jsx("div", { className: style.skillsEmpty, children: ze('暂无 Skill', 'No skills yet') }));
        }
        return (_jsxs(_Fragment, { children: [regularPlatformItems.length > 0 && (_jsxs("div", { className: style.skillsSection, children: [_jsx("div", { className: style.skillsSectionTitle, children: ze('平台官方技能', 'Official Platform Skills') }), _jsx("div", { className: style.skillsGrid, children: regularPlatformItems.map((item, idx) => renderSkillCard(item, idx)) })] })), autoEnabledItems.length > 0 && (_jsxs("div", { className: style.skillsSection, children: [_jsx("div", { className: style.skillsSectionTitle, children: ze('平台默认开启（自动注入，仅可查看）', 'Auto-enabled by Platform (view only)') }), _jsx("div", { className: style.skillsGrid, children: autoEnabledItems.map((item, idx) => renderSkillCard(item, idx)) })] }))] }));
    }
    // -------------------- Main render --------------------
    const isAdminTab = filterTab === 'admin_default';
    return (_jsxs("div", { className: style.sectionBody, children: [_jsxs("div", { className: style.skillsOpBar, children: [_jsx("div", { className: style.skillsFilterTabs, children: FILTER_TABS
                            .filter(tab => !tab.adminOnly || isSuperAdmin)
                            .map(tab => (_jsx("button", { className: `${style.skillsFilterTab} ${filterTab === tab.key ? style.skillsFilterTabActive : ''}`, onClick: () => setFilterTab(tab.key), children: tab.label }, tab.key))) }), isAdminTab ? (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: openAdminCreateModal, children: ze('创建默认 Skill', 'Create Default Skill') })) : (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: openCreateModal, children: ze('上传 Skill', 'Upload Skill') }))] }), isAdminTab && (_jsxs(_Fragment, { children: [_jsx(Alert, { type: "info", showIcon: true, banner: true, message: ze('此处管理的 Skill 会自动对所有用户生效，无需用户手动启用。请谨慎操作。', 'Skills managed here are automatically enabled for all users. Please operate with caution.'), style: { marginBottom: 12 } }), adminSkills.length === 0 ? (_jsx("div", { className: style.skillsEmpty, children: ze('暂无默认平台 Skill', 'No default platform skills yet') })) : (_jsx("div", { className: style.skillsGrid, children: adminSkills.map(skill => renderAdminSkillCard(skill)) }))] })), !isAdminTab && (_jsx(_Fragment, { children: loading ? (_jsx("div", { className: style.skillsEmpty, children: ze('加载中…', 'Loading…') })) : filteredList.length === 0 ? (_jsx("div", { className: style.skillsEmpty, children: ze('暂无 Skill', 'No skills yet') })) : filterTab === 'all' ? (
                // 配置流程简化：'全部' tab 下按"平台技能 / 自定义技能"分两区展示
                (() => {
                    const platformItems = filteredList.filter(i => i.type !== 'custom');
                    const customItems = filteredList.filter(i => i.type === 'custom');
                    const { autoEnabledItems, regularPlatformItems } = splitPlatformDisplayItems(platformItems);
                    return (_jsxs(_Fragment, { children: [(regularPlatformItems.length > 0 || autoEnabledItems.length > 0) && (_jsxs("div", { className: style.skillsSection, children: [_jsx("div", { className: style.skillsSectionTitle, children: ze('平台技能（默认开启，不可关闭）', 'Platform Skills (enabled by default, cannot be disabled)') }), regularPlatformItems.length > 0 && (_jsx("div", { className: style.skillsGrid, children: regularPlatformItems.map((item, idx) => renderSkillCard(item, idx)) })), autoEnabledItems.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: style.skillsSubSectionTitle, children: ze('平台默认开启（自动注入，仅可查看）', 'Auto-enabled by Platform (view only)') }), _jsx("div", { className: style.skillsGrid, children: autoEnabledItems.map((item, idx) => renderSkillCard(item, idx)) })] }))] })), _jsxs("div", { className: style.skillsSection, children: [_jsx("div", { className: style.skillsSectionTitle, children: ze('自定义技能', 'Custom Skills') }), customItems.length > 0 ? (_jsx("div", { className: style.skillsGrid, children: customItems.map((item, idx) => renderSkillCard(item, idx)) })) : (_jsx("div", { className: style.skillsEmpty, children: ze('暂无自定义技能，点击右上角「上传 Skill」添加', 'No custom skills yet. Click "Upload Skill" to add one') }))] })] }));
                })()) : filterTab === 'platform' ? (renderPlatformSkillSections(filteredList)) : (_jsx("div", { className: style.skillsGrid, children: filteredList.map((item, idx) => renderSkillCard(item, idx)) })) })), _jsxs(Modal, { title: getModalTitle(), open: modalVisible, onCancel: closeModal, maskClosable: modalMode === 'preview', width: 600, destroyOnClose: true, footer: getModalFooter(), children: [modalMode === 'preview' && renderPreviewContent(), (modalMode === 'create' || modalMode === 'edit') && renderFormContent()] }), _jsxs(Modal, { title: getAdminModalTitle(), open: adminModalVisible, onCancel: closeAdminModal, maskClosable: adminModalMode === 'preview', width: 700, destroyOnClose: true, footer: getAdminModalFooter(), children: [adminModalMode === 'preview' && renderAdminPreviewContent(), (adminModalMode === 'create' || adminModalMode === 'edit') && renderAdminFormContent()] }), _jsxs(Modal, { title: confirmTitle, open: confirmModalVisible, onCancel: () => setConfirmModalVisible(false), destroyOnClose: true, footer: _jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: () => setConfirmModalVisible(false), children: ze('取消', 'Cancel') }), _jsx(Button, { type: "primary", danger: true, disabled: confirmInput !== confirmSkillName, onClick: executeConfirmAction, children: ze('确认执行', 'Confirm') })] }), children: [_jsx("div", { style: { marginBottom: 16 }, children: ze('此操作会影响所有用户。请输入以下 Skill 名称以确认操作：', 'This operation affects all users. Please type the following skill name to confirm:') }), _jsx("div", { style: { fontWeight: 600, fontSize: 15, marginBottom: 12, padding: '8px 12px', background: '#f5f5f5', borderRadius: 6 }, children: confirmSkillName }), _jsx(Input, { placeholder: ze('输入上方名称以确认', 'Type the name above to confirm'), value: confirmInput, onChange: (e) => setConfirmInput(e.target.value) })] })] }));
};
export default CrashClawSkillsPanel;
